import { useEffect, useState } from "react";
import useSWR from "swr";
import { getMonthlyStatistics } from "@/services/statistics.service";

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function getUtcMonthKey() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${now.getUTCMonth()}`;
}

/** The UTC calendar window and request state shared by both usage charts. */
export function useRollingMonthlyUsage(enabled: boolean) {
  // SWR need not rerender when a poll returns identical data. Refresh the
  // calendar at month-end even then, without rerendering every polling tick.
  const [, setCalendarMonth] = useState(getUtcMonthKey);
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setCalendarMonth(getUtcMonthKey()), 60000);
    return () => clearInterval(timer);
  }, [enabled]);

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();
  const needsPreviousYear = currentMonth < 11;

  const { data: currentResponse, error: currentError, isLoading: currentLoading } = useSWR(
    enabled ? `monthly-stats-${currentYear}` : null,
    () => getMonthlyStatistics(currentYear),
    { refreshInterval: 60000 },
  );
  const { data: previousResponse, error: previousError, isLoading: previousLoading } = useSWR(
    enabled && needsPreviousYear ? `monthly-stats-${currentYear - 1}` : null,
    () => getMonthlyStatistics(currentYear - 1),
    { refreshInterval: 60000 },
  );

  const isUnavailable = enabled && (
    Boolean(currentError) || currentResponse?.success === false ||
    (needsPreviousYear && (Boolean(previousError) || previousResponse?.success === false))
  );
  const isLoading = enabled && !isUnavailable && (
    currentLoading || !currentResponse ||
    (needsPreviousYear && (previousLoading || !previousResponse))
  );
  const allStats = [
    ...(needsPreviousYear && previousResponse?.success ? previousResponse.data : []),
    ...(currentResponse?.success ? currentResponse.data : []),
  ];
  const data = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(Date.UTC(currentYear, currentMonth - 11 + i, 1));
    const period = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
    return {
      name: monthNames[date.getUTCMonth()],
      period,
      stats: allStats.find(stat => stat.period === period),
    };
  });

  return { data, isLoading, isUnavailable };
}
